import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { baseQueryWithStatusCheck } from '@/store/baseQueryWithStatusCheck';

export type AlarmsQueryParams = {
  metric: 'duration' | 'count';
  chart_group_by?: 'components' | 'alarms' | 'assets';
  group_by?: 'components' | 'alarms' | 'assets';
  top_ranked?: number;
  asset_types?: string[] | null;
  from_date?: string;
  to_date?: string;
  priority?: string[];
  asset_ids?: string[] | null;
  component_ids?: string[] | null;
  severity?: string[];
  add_metadata?: boolean;
  alarm_interface?: string[];
  disable_weekly_aggregation?: boolean;
};

export enum AlarmsFilter {
  PRIORITY = 'priority',
  SEVERITY = 'severity',
  INTERFACE = 'interface',
}

type AlarmsFiltersQueryParams = {
  from_date: string;
  to_date: string;
  filters: AlarmsFilter[];
  asset_types?: string[] | null;
  asset_ids?: string[] | null;
  component_ids?: string[] | null;
};

const baseQuery = retry(baseQueryWithStatusCheck, { maxRetries: 2 });

export const alarmsApi = createApi({
  reducerPath: 'alarmsApi',
  baseQuery,
  tagTypes: ['TopAlarms', 'SummarizedAlarms', 'ExternalAlarms', 'AlarmsFilters'],
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getTopAlarms: builder.query<any, any>({
      query: ({
        metric,
        chart_group_by,
        top_ranked = 7,
        asset_types,
        from_date,
        to_date,
        priority,
        asset_ids = null,
        component_ids = null,
        severity,
        alarm_interface,
      }: AlarmsQueryParams) => {
        const searchParams = new URLSearchParams();
        const params: Partial<AlarmsQueryParams> = {
          metric,
          chart_group_by,
          top_ranked,
          asset_types,
          from_date,
          to_date,
          priority,
          asset_ids,
          component_ids,
          severity,
          alarm_interface,
        };

        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                if (key === 'chart_group_by') {
                  searchParams.append('group_by', value.join(','));
                } else searchParams.append(key, value.join(','));
              }
            } else {
              if (key === 'chart_group_by') {
                searchParams.append('group_by', String(value));
              } else {
                searchParams.append(key, String(value));
              }
            }
          }
        }
        return { url: `alarm/top_alarms?${searchParams.toString()}` };
      },
      providesTags: [{ type: 'TopAlarms' }],
    }),
    getSummarizedAlarms: builder.query<any, any>({
      query: ({
        metric,
        chart_group_by,
        asset_types,
        from_date,
        group_by = 'alarms',
        to_date,
        priority,
        asset_ids = null,
        component_ids = null,
        severity,
        alarm_interface,
      }: Partial<AlarmsQueryParams> & { limit: number }) => {
        const searchParams = new URLSearchParams();

        const params: Partial<AlarmsQueryParams> = {
          metric,
          chart_group_by,
          asset_types,
          from_date,
          group_by,
          to_date,
          priority,
          asset_ids,
          component_ids,
          severity,
          alarm_interface,
          disable_weekly_aggregation: true,
        };

        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              searchParams.append(key, String(value));
            }
          }
        }
        return { url: `alarm/summarized_alarms?${searchParams.toString()}` };
      },
      providesTags: [{ type: 'SummarizedAlarms' }],
    }),
    getExternalAlarms: builder.query<any, any>({
      query: ({
        metric,
        top_ranked = 20,
        component_ids,
        group_by,
        from_date,
        to_date,
        priority,
        asset_ids,
        severity,
        add_metadata = true,
        alarm_interface,
      }: Partial<AlarmsQueryParams> & { limit: number }) => {
        const searchParams = new URLSearchParams();

        const params: Partial<AlarmsQueryParams> = {
          metric,
          top_ranked,
          component_ids,
          group_by,
          from_date,
          to_date,
          priority,
          asset_ids,
          severity,
          add_metadata,
          alarm_interface,
        };

        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              searchParams.append(key, String(value));
            }
          }
        }
        return { url: `alarm/external?${searchParams.toString()}` };
      },
      providesTags: [{ type: 'ExternalAlarms' }],
    }),
    getAlarmsFilters: builder.query<any, any>({
      query: ({ from_date, to_date, filters, asset_ids, component_ids, asset_types }: AlarmsFiltersQueryParams) => {
        const searchParams = new URLSearchParams();
        const params = {
          from_date,
          to_date,
          filters,
          asset_ids,
          component_ids,
          asset_types,
        };

        for (const [key, value] of Object.entries(params)) {
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              if (value.length > 0) {
                searchParams.append(key, value.join(','));
              }
            } else {
              searchParams.append(key, String(value));
            }
          }
        }
        return { url: `alarm/filters?${searchParams.toString()}` };
      },
      providesTags: [{ type: 'AlarmsFilters' }],
    }),
  }),
});

export const {
  useGetTopAlarmsQuery,
  useLazyGetTopAlarmsQuery,
  useGetSummarizedAlarmsQuery,
  useLazyGetSummarizedAlarmsQuery,
  useGetExternalAlarmsQuery,
  useLazyGetExternalAlarmsQuery,
  useGetAlarmsFiltersQuery,
  useLazyGetAlarmsFiltersQuery,
} = alarmsApi;
